import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, Image as ImageIcon, Paperclip,
  Trash2, X, Download, Loader2, AlertTriangle,
  CheckCircle2, MapPin, ChevronLeft, ChevronRight,
  FolderInput, Upload, Clock,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cachePhoto, queueItem, claimHopUpload, releaseHopUpload, completeHopUpload, patchHopArgs } from "@/lib/offline";   // T-401; save-first 10 Oct 2026
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { useHopLocal, HopCategoryBanner, HopPendingTiles, type HopLocal } from "./HopLocalStatus";   // 10 Oct 2026 — today on this phone
import { uploadHopAsset, getAssetSignedUrl, getHopTiles, makeTilesForOlderPhotos, deleteHopAsset } from "./hopUpload";
import { getGpsCoords } from "./hopWatermark";
import { useGps } from "@/features/gps/useGps";
import { useAcceptanceBasket } from "@/features/acceptance-hub/useAcceptanceBasket";
import { Link } from "react-router-dom";
import { ShieldCheck, ShoppingBag, Share2, Send, Check } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sendProgressUpdate, fetchAlreadyShared } from "./shareRelease";
import { useHopShareStore } from "./useHopShareStore";

// =============================================================================
// HopCategoryScreen — /projects/:projectId/hop/:packId/categories/:packCategoryId
// T-010 HOP Commit C.2 — field capture surface.
//
// One screen per pack-category. Renders subcategories as cards with:
//   * existing photo thumbnails + doc rows
//   * three upload buttons: Camera (capture=environment), Photos (multi-
//     select from gallery), Document (PDF/etc.)
//   * Per-subcategory progress indicator while uploading
//
// For per-instance categories (Sector / Microwave / Active Antenna), an
// instance tab bar lets the user pick which instance (S1 / S2 / S3) all
// uploads on this view target.
//
// Lightbox renders signed-URL full-size for any tapped photo.
//
// 10 Oct 2026 — SAVE FIRST, UPLOAD SECOND (Nullarbor: photos taken on one bar
// of signal went missing). Every photo is written to the phone and put on the
// offline queue the moment it is picked — before the GPS wait, before any
// network. Only then is the upload tried; if it works the photo comes off the
// queue, if it does not it is already safe and lib/offline's HOP lane keeps
// trying. The bar at the bottom now says "uploaded" or "on this phone" — it
// used to say "saved" for both.
//
// 10 Oct 2026 (later) — that bottom bar is GONE. It sat over the phone's bottom
// nav (James's screenshot: "the nav bar looks gone and its untidy") and, once
// photos could wait on the phone, it never went away. Its job moved to the top
// of the page: <HopCategoryBanner> (taken / uploaded / on this phone today,
// the upload in progress, anything NOT SAVED) and <HopPendingTiles> inside
// each subcategory (the waiting photos themselves). Both read the phone, not
// Ops, so they are right with no signal — features/hop/HopLocalStatus.tsx.
//
// mig 441 — what goes up from here first is the QUICK copy (under 3 MP); the
// full-size copy follows from the queue. The lightbox gets a "Full size"
// button once a photo's full copy has landed. That list is read in its own
// small query so this screen still loads on a database without the column.
//
// mig 442 — the grid draws small TILE copies, signed for the whole page in one
// call (was: the photo itself, one signing call per photo). Tapping a photo
// opens the real one, signed then. Managers get "Make small tiles" for photos
// from before tiles existed. And (James, 10 Oct): "a slight shading for photos
// that were taken on a previous day so the guys taking can quickly see whos
// are old vs the ones they took that day" — earlier-day tiles are dimmed and
// carry their date, but only on a page that also has photos from today.
// =============================================================================

type AssetType = "photo" | "document";

interface PackBrief {
  pack_id:      string;
  pack_number:  string;
  status:       string;
  label_prefix: string | null;
  project?:     { project_id: string; project_code: string; project_name: string } | null;
}

interface PackCategory {
  pack_category_id: string;
  pack_id:      string;
  code:         string;
  display_name: string;
  description:  string | null;
  required_qty: number;
  is_custom:    boolean;
}

interface PackSubcategory {
  pack_subcategory_id: string;
  pack_category_id:    string;
  code:                string;
  display_name:        string;
  hint:                string | null;
  allow_free_text:     boolean;
  sort_order:          number;
  is_na:               boolean;
}

interface PackInstance {
  instance_id:      string;
  instance_code:    string;
  instance_label:   string | null;
  sort_order:       number;
}

interface Asset {
  asset_id:           string;
  pack_subcategory_id: string | null;
  instance_id:        string | null;
  asset_type:         AssetType;
  storage_path:       string;
  mime_type:          string | null;
  file_size_bytes:    number | null;
  file_name:          string | null;
  description:        string | null;
  gps_lat:            number | null;
  gps_lng:            number | null;
  gps_source:         "exif" | "device_current" | null;   // T-199
  taken_at:           string | null;
  captured_at:        string;
  watermarked:        boolean;
  captured_by_employee_id: string | null;
}

// T-413 review fix — this set MUST match HopNewPackScreen's: the cabinet
// family was missing, so packs with BBS001/CAB001 instances rendered NO
// instance tabs here and photos uploaded with instance_id = null (identifier
// lost from export filenames).
const PER_INSTANCE_CODES = new Set([
  "sector", "active_antenna", "microwave",
  "cabinet", "rbs_cabinet", "shelter", "bbs_cabinet",
]);

interface UploadJob {
  id:         string;
  filename:   string;
  phase:      "queued" | "watermark" | "upload" | "save" | "done" | "held" | "error";   // held = safe on this phone, not uploaded yet
  pct:        number;
  errMsg?:    string;
}

// 10 Oct 2026 — one upload at a time across the whole screen. Each Camera tap
// starts its own handleFiles; tapping fast used to start that many uploads at
// once, each unpacking a 12 MP photo in memory and all sharing one bar of
// signal. Photos are still SAVED the instant they are taken (pass 1 does not
// wait); only the sending takes turns.
let hopUploadTurn: Promise<void> = Promise.resolve();

// Local calendar day of a timestamp — never toISOString().slice (shifts in AEST).
function dayKeyOf(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// -----------------------------------------------------------------------------

export default function HopCategoryScreen() {
  const { projectId, packId, packCategoryId } = useParams<{
    projectId: string; packId: string; packCategoryId: string;
  }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canUpload = hasRole(user, "manager") || true; // any team member; RLS does the real gate
  const canBasket = hasRole(user, "manager"); // T-160 — only manager+ can build acceptance pack
  const canManage = hasRole(user, "manager"); // T-220 — manager+ for delete on submitted packs

  // T-262 — start watching GPS from page mount so a fix is ready by the time
  // the user taps Camera (avoids the iOS one-shot-after-capture quirk window).
  const gpsHook = useGps(true);

  const [activeInstanceId, setActiveInstanceId] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadJob[]>([]);
  const hopLocalData = useHopLocal(packId);   // 10 Oct 2026 — what this phone holds for this pack
  // T-215 — siblings + index let lightbox prev/next nav through subcategory.
  const [lightbox, setLightbox] = useState<{ siblings: Asset[]; index: number } | null>(null);
  // T-218 — move-to-category modal target (null = closed)
  const [moveTarget, setMoveTarget] = useState<Asset | null>(null);

  // T-160 — acceptance pack basket. Manager toggles photos in/out across
  // categories; the basket persists per-project via a draft hop_submission.
  const { basketAssetIds, basketSize, toggle: toggleBasket, addMany: addManyToBasket, clear: clearBasket } = useAcceptanceBasket(projectId);

  // Stage B — "Share to client" selection. Persistent across categories AND
  // packs for this project (like the acceptance basket) via a Zustand store, so
  // one progress update can span the whole site. Off by default.
  const shareStore = useHopShareStore();
  useEffect(() => { if (projectId) shareStore.ensureProject(projectId); }, [projectId]); // eslint-disable-line
  const shareMode = shareStore.active;
  const shareSel = shareStore.selected;
  const [sendOpen, setSendOpen] = useState(false);
  const { data: alreadyShared = new Set<string>(), refetch: refetchShared } = useQuery({
    queryKey: ["hop_already_shared", projectId],
    enabled: !!projectId && (shareMode || shareSel.size > 0),
    queryFn: () => fetchAlreadyShared(projectId!),
    staleTime: 15 * 1000,
  });
  function toggleShare(assetId: string) {
    if (alreadyShared.has(assetId)) return; // already in the portal — skip
    shareStore.toggle(assetId);
  }
  function addManyShare(assetIds: string[]) {
    shareStore.addMany(assetIds.filter(id => !alreadyShared.has(id)));
  }
  function exitShareMode() { shareStore.exit(); }

  // -- Load context ---------------------------------------------------------
  const { data: pack } = useQuery({
    queryKey: ["hop_pack_brief", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`pack_id, pack_number, status, label_prefix,
                 project:project_id(project_id, project_code, project_name)`)
        .eq("pack_id", packId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as PackBrief | null;
    },
  });

  const { data: category, isLoading: loadingCat } = useQuery({
    queryKey: ["hop_pack_category", packCategoryId],
    enabled: !!packCategoryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_categories")
        .select("pack_category_id, pack_id, code, display_name, description, required_qty, is_custom")
        .eq("pack_category_id", packCategoryId!)
        .maybeSingle();
      if (error) throw error;
      return data as PackCategory | null;
    },
  });

  const { data: subcategories = [], refetch: refetchSubcats } = useQuery({
    queryKey: ["hop_pack_subcats_for_cat", packCategoryId],
    enabled: !!packCategoryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_subcategories")
        .select("pack_subcategory_id, pack_category_id, code, display_name, hint, allow_free_text, sort_order, is_na")
        .eq("pack_category_id", packCategoryId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackSubcategory[];
    },
  });

  // Mark a subcategory N/A (not required for this specific pack) or restore it.
  async function setSubcatNa(packSubcategoryId: string, na: boolean) {
    await supabase.from("hop_pack_subcategories").update({ is_na: na }).eq("pack_subcategory_id", packSubcategoryId);
    await refetchSubcats();
  }

  const { data: instances = [] } = useQuery({
    queryKey: ["hop_pack_instances_for_cat", packCategoryId],
    enabled: !!packCategoryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_category_instances")
        .select("instance_id, instance_code, instance_label, sort_order")
        .eq("pack_category_id", packCategoryId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackInstance[];
    },
  });

  const isPerInstance = !!category && PER_INSTANCE_CODES.has(category.code);

  // Auto-select first instance when they load
  useEffect(() => {
    if (isPerInstance && instances.length > 0 && !activeInstanceId) {
      setActiveInstanceId(instances[0].instance_id);
    }
    if (!isPerInstance) setActiveInstanceId(null);
  }, [isPerInstance, instances, activeInstanceId]);

  // -- Load assets ----------------------------------------------------------
  const { data: assets = [], refetch: refetchAssets } = useQuery({
    queryKey: ["hop_assets_for_cat", packCategoryId, activeInstanceId, isPerInstance],
    enabled: !!packCategoryId,
    queryFn: async () => {
      let q = supabase
        .from("hop_assets")
        .select(`asset_id, pack_subcategory_id, instance_id, asset_type, storage_path,
                 mime_type, file_size_bytes, file_name, description,
                 gps_lat, gps_lng, gps_source, taken_at, captured_at, watermarked,
                 captured_by_employee_id`)
        .eq("pack_category_id", packCategoryId!)
        .order("captured_at", { ascending: false });
      if (isPerInstance && activeInstanceId) q = q.eq("instance_id", activeInstanceId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Asset[];
    },
  });

  // -- Resolve signed URLs in batch -----------------------------------------
  const photoAssets = useMemo(() => assets.filter(a => a.asset_type === "photo"), [assets]);
  // mig 442 — one call signs every tile on the page. Keyed by asset_id.
  const { data: tiles = { urls: new Map<string, string>(), noTile: [] as string[] }, refetch: refetchTiles } = useQuery({
    queryKey: ["hop_tiles", photoAssets.map(a => a.asset_id).join("|")],
    enabled: photoAssets.length > 0,
    queryFn: () => getHopTiles(photoAssets),
    staleTime: 25 * 60 * 1000, // refresh well before 30 min expiry
  });
  const signedUrls = tiles.urls;
  const [tileJob, setTileJob] = useState<{ done: number; total: number } | null>(null);
  async function makeOlderTiles() {
    const todo = photoAssets.filter(a => tiles.noTile.includes(a.asset_id));
    if (todo.length === 0 || tileJob) return;
    setTileJob({ done: 0, total: todo.length });
    try {
      const r = await makeTilesForOlderPhotos(packId!, todo, (done, total) => setTileJob({ done, total }));
      if (r.failed > 0) window.alert(`${r.made} done. ${r.failed} could not be made — those photos keep loading the old way. Try again on a better connection.`);
    } finally { setTileJob(null); void refetchTiles(); }
  }

  // mig 441 — which photos here have a full-size copy (own query, fails soft).
  const { data: fullPaths = new Map<string, string>() } = useQuery({
    queryKey: ["hop_full_paths", packCategoryId, assets.length],
    enabled: !!packCategoryId && assets.length > 0,
    retry: false,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("hop_assets")
        .select("asset_id, full_storage_path")
        .eq("pack_category_id", packCategoryId!)
        .not("full_storage_path", "is", null);
      if (error) return new Map<string, string>();
      return new Map<string, string>(((data ?? []) as any[]).map(r => [r.asset_id, r.full_storage_path]));
    },
  });

  // -- Group assets by subcategory ------------------------------------------
  const assetsBySubcat = useMemo(() => {
    const m = new Map<string, Asset[]>();
    for (const a of assets) {
      const key = a.pack_subcategory_id ?? "_unsorted";
      const arr = m.get(key) ?? [];
      arr.push(a);
      m.set(key, arr);
    }
    return m;
  }, [assets]);

  // T-279 — flat list of photo asset_ids currently visible on this page
  // (across all subcategories for the active instance). Feeds the
  // "Select all on page" button + its unselected-count badge.
  const pagePhotoAssetIds = useMemo(() => {
    const ids: string[] = [];
    for (const arr of assetsBySubcat.values()) {
      for (const a of arr) if (a.asset_type === "photo") ids.push(a.asset_id);
    }
    return ids;
  }, [assetsBySubcat]);
  const pageUnselectedCount = useMemo(
    () => pagePhotoAssetIds.filter(id => !basketAssetIds.has(id)).length,
    [pagePhotoAssetIds, basketAssetIds],
  );

  // -- Upload pipeline ------------------------------------------------------
  async function handleFiles(
    files: FileList | null,
    packSubcategoryId: string,
    subcategoryCode: string,
    opts: { isLiveCapture?: boolean; skipWatermark?: boolean } = {},
  ) {
    if (!files || files.length === 0) return;
    // T-320 — per-instance categories (Sector / Active Antenna / Microwave) MUST
    // have an instance tab selected, or the photo lands with a null instance and
    // its filename loses the S1/M1/K1 identifier (and collides with siblings).
    if (isPerInstance && !activeInstanceId) {
      alert("Pick an instance tab (S1 / M1 / K1 …) above before adding photos, so each one is labelled to the right antenna/sector/link.");
      return;
    }
    const fileArr = Array.from(files);
    // 10 Oct 2026 — the moment of capture, taken BEFORE anything can wait.
    const capturedAtIso = opts.isLiveCapture ? new Date().toISOString() : null;

    const jobs: UploadJob[] = fileArr.map(f => ({
      id: `${Date.now()}_${f.name}_${Math.random().toString(36).slice(2, 8)}`,
      filename: f.name,
      phase: "queued",
      pct: 0,
    }));
    setUploads(prev => [...prev, ...jobs]);
    const updateJobById = (id: string, patch: Partial<UploadJob>) =>
      setUploads(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));

    const activeInstance = instances.find(x => x.instance_id === activeInstanceId);
    const baseArgs = {
      packId:              packId!,
      packCategoryId:      packCategoryId!,
      packSubcategoryId,
      instanceId:          activeInstanceId,
      labelPrefix:         pack?.label_prefix ?? null,
      subcategoryCode,
      instanceCode:        activeInstance?.instance_code ?? null,
      employeeId:          user?.employee_id ?? null,
      // Operator identity is captured via captured_by_employee_id in the
      // audit trail. Watermark stays focused on label + timestamp + GPS.
      employeeName:        null,
      // T-262 — live capture → the device fix (patched in below once known);
      // library uploads stay EXIF-only (undefined-vs-null matters downstream:
      // null here still means "live capture, no fix").
      precomputedGps:      null as { lat: number; lng: number } | null,
      skipWatermark:       opts.skipWatermark ?? false,
      capturedAtIso,
    };

    // ---- PASS 1: every photo onto the phone + the queue. No network. --------
    // Claimed first so the background sync leaves them to this screen.
    const saved: { local_id: string; queueId: number | null; clientUploadId: string }[] = [];
    for (let i = 0; i < fileArr.length; i++) {
      const file = fileArr[i];
      const local_id = `hop_${jobs[i].id}`;
      const clientUploadId = `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
      claimHopUpload(local_id);
      let queueId: number | null = null;
      try {
        await cachePhoto(local_id, file, { file_name: file.name });
        queueId = await queueItem("hop_asset", {
          local_id,
          file_name:    file.name,
          content_type: file.type,
          args:         { ...baseArgs, clientUploadId },
        });
      } catch (e) {
        // Phone storage full / blocked. The upload is still tried below, but
        // this photo has no safety net — the bar says so if that fails too.
        console.warn("[HOP] could not save the photo on the phone", e);
      }
      saved.push({ local_id, queueId, clientUploadId });
    }

    // T-262 — iPhone's <input capture="environment"> strips EXIF, so a live
    // camera capture lands at uploadHopAsset with no EXIF GPS. Pre-fetch one
    // live GPS fix (denied/timeout → null gracefully) and pass it down.
    // 10 Oct 2026: this wait (up to 15 s) now happens AFTER the photos are
    // safe, and the fix is written onto the queued items as well.
    let liveGps: { lat: number; lng: number } | null = null;
    if (opts.isLiveCapture) {
      if (gpsHook.fix) {
        // Watched fix ready (page has been open a few seconds) — use it directly
        liveGps = { lat: gpsHook.fix.latitude, lng: gpsHook.fix.longitude };
      } else {
        // Page just opened, watcher hasn't fixed yet — one-shot fallback with longer timeout
        liveGps = await getGpsCoords(15_000);
      }
      if (liveGps) {
        for (const sv of saved) {
          if (sv.queueId != null) await patchHopArgs(sv.queueId, { precomputedGps: liveGps }).catch(() => {});
        }
      }
    }

    // ---- PASS 2: upload, one at a time. ------------------------------------
    // ...and one batch at a time: wait for any earlier tap's uploads to finish.
    const earlier = hopUploadTurn;
    let endTurn: () => void = () => {};
    hopUploadTurn = new Promise<void>(r => { endTurn = r; });
    await earlier;
    try {
    for (let i = 0; i < fileArr.length; i++) {
      const file = fileArr[i];
      const job = jobs[i];
      const sv = saved[i];
      const isSaved = sv.queueId != null;
      const updateJob = (patch: Partial<UploadJob>) => updateJobById(job.id, patch);
      const hopArgs = {
        ...baseArgs,
        precomputedGps: opts.isLiveCapture ? (liveGps ?? null) : null,
        clientUploadId: sv.clientUploadId,
      };
      const notUploaded = (why: string) => updateJob(isSaved
        ? { phase: "held", pct: 100, errMsg: "on this phone" }
        : { phase: "error", errMsg: `NOT SAVED — take it again (${why})` });

      try {
        if (!navigator.onLine) { notUploaded("no signal"); continue; }
        updateJob({ phase: "watermark", pct: 0 });
        const res = await uploadHopAsset({
          ...hopArgs,
          file,
          onProgress: (phase, pct) => updateJob({ phase, pct }),
        });
        // mig 441 — that was the quick copy. If the photo was shrunk for it,
        // the queue item stays to send the full-size copy later.
        if (isSaved) await completeHopUpload(sv.queueId!, sv.local_id, res.full_pending ? { asset_id: res.asset_id } : undefined).catch(() => {});
        updateJob({ phase: "done", pct: 100 });
      } catch (e: any) {
        console.warn("[HOP] upload did not go through — the photo stays queued on the phone", e);
        notUploaded(e?.message ?? "upload failed");
      } finally {
        releaseHopUpload(sv.local_id);   // from here the background sync owns it
      }
    }
    } finally { endTurn(); }

    // Refresh asset list once all done
    await refetchAssets();
    qc.invalidateQueries({ queryKey: ["hop_asset_counts", packId] });

    // Auto-clear successful jobs after a beat so the bar isn't permanent
    // 10 Oct 2026 — photos still on the phone stay on the bar a little longer
    // (the "waiting to upload" badge carries them after that); errors stay.
    // (later) photos still on the phone are carried by the banner + the
    // "on this phone" tiles from the queue itself; only errors stay as jobs,
    // until the crew clears them.
    setTimeout(() => {
      setUploads(prev => prev.filter(j => j.phase === "error"));
    }, 1500);
  }

  // -- Render --------------------------------------------------------------
  if (!user) return null;
  if (loadingCat) return <div className="p-6 flex justify-center"><Spinner size={20}/></div>;
  if (!category) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Card>
          <EmptyState
            title="Category not found"
            description="This HOP category doesn't exist or you don't have access to it."
            icon={Camera}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-32">
      <button
        onClick={() => navigate(`/projects/${projectId}/hop/${packId}`)}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> Back to pack
      </button>

      {/* T-262 — visible GPS status chip. Watcher starts on mount so a fix is
          usually ready before the user taps Camera; surfaces denied/timeout so
          the user (and we) can diagnose live-capture GPS failures at a glance. */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-mono ${
          gpsHook.status === "fix"
            ? "bg-ok/10 text-ok border border-ok/30"
            : gpsHook.status === "requesting"
              ? "bg-accent/10 text-accent border border-accent/30"
              : "bg-danger/10 text-danger border border-danger/30"
        }`}>
          <MapPin size={11} />
          {gpsHook.status === "fix" && gpsHook.fix
            ? `GPS ready (±${Math.round(gpsHook.fix.accuracy_m)}m)`
            : gpsHook.status === "requesting"
              ? "GPS locating…"
              : gpsHook.status === "denied"
                ? "Location denied — enable in Safari settings"
                : gpsHook.status === "unavailable"
                  ? "GPS unavailable (no signal)"
                  : gpsHook.status === "timeout"
                    ? "GPS timeout"
                    : "GPS idle"}
        </span>
        {(gpsHook.status === "denied" || gpsHook.status === "timeout" || gpsHook.status === "unavailable" || gpsHook.status === "idle") && (
          <button
            type="button"
            onClick={() => gpsHook.refresh()}
            className="text-[10px] text-accent hover:underline"
          >
            Retry GPS
          </button>
        )}
      </div>

      {/* 10 Oct 2026 — today on this phone, for this category. */}
      <HopCategoryBanner
        local={hopLocalData}
        packCategoryId={packCategoryId!}
        jobs={uploads}
        onDismissErrors={() => setUploads(prev => prev.filter(j => j.phase !== "error"))}
        onSent={() => { void refetchAssets(); qc.invalidateQueries({ queryKey: ["hop_asset_counts", packId] }); }}
      />

      {/* mig 442 — photos from before tiles existed still load full size. */}
      {canManage && tiles.noTile.length > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-line bg-ink-raised">
          <div className="text-[12px] text-text-muted">
            <span className="font-mono text-text">{tiles.noTile.length}</span> older photo{tiles.noTile.length === 1 ? "" : "s"} here still load{tiles.noTile.length === 1 ? "s" : ""} at full size, which is what makes this page slow.
            {tileJob && <span className="text-accent"> Making tiles… {tileJob.done}/{tileJob.total}</span>}
          </div>
          <Button variant="secondary" size="sm" type="button" disabled={!!tileJob} onClick={() => { void makeOlderTiles(); }}
            title="Downloads each photo once on this computer and saves a small copy for the grid. Do it on wifi.">
            {tileJob ? "Working…" : "Make small tiles"}
          </Button>
        </div>
      )}

      {/* T-160 — basket indicator. Pinned bottom-right while photos are
          ticked. Click "Continue" to assemble the submission. */}
      {canBasket && !shareMode && basketSize > 0 && (
        <div className="fixed right-4 z-40 bottom-[calc(var(--nav-clear)+var(--sticky-bar-h,0px)+1rem)] md:bottom-24 bg-ink-card border border-accent/40 rounded-lg shadow-xl p-3 flex items-center gap-3 max-w-sm">
          <div className="w-9 h-9 rounded-full bg-accent text-white flex items-center justify-center font-semibold flex-shrink-0">
            {basketSize}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium text-text leading-tight">
              Acceptance pack
            </div>
            <div className="text-[11px] text-text-muted">
              {basketSize} photo{basketSize === 1 ? "" : "s"} · across packs
            </div>
          </div>
          {/* T-279 — Cancel/clear-basket. Single DELETE on hop_submissions
              (CASCADE wipes the photos) — way cleaner than untoggling N times. */}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (window.confirm(`Discard the ${basketSize} photo${basketSize === 1 ? "" : "s"} in this acceptance pack? This clears the basket — you can start over.`)) {
                void clearBasket();
              }
            }}
            type="button"
          >
            Cancel
          </Button>
          <Link to={`/projects/${projectId}/hop/submit/new`}>
            <Button variant="primary" size="sm" icon={ShieldCheck}>
              Continue
            </Button>
          </Link>
        </div>
      )}

      {/* Stage B — floating "send progress update" card. Shows whenever a
          selection exists (follows you across categories + packs), not just on
          the page where you started ticking. */}
      {shareSel.size > 0 && (
        <div className="fixed right-4 z-40 bottom-[calc(var(--nav-clear)+var(--sticky-bar-h,0px)+1rem)] md:bottom-24 bg-ink-card border border-emerald-500/40 rounded-lg shadow-xl p-3 flex items-center gap-3 max-w-sm">
          <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center font-semibold flex-shrink-0">
            {shareSel.size}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium text-text leading-tight">Progress update</div>
            <div className="text-[11px] text-text-muted">{shareSel.size} photo{shareSel.size === 1 ? "" : "s"} · across the site</div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => shareStore.clear()} type="button">Clear</Button>
          <Button variant="primary" size="sm" icon={Send} onClick={() => setSendOpen(true)} type="button">Send…</Button>
        </div>
      )}

      {/* Header */}
      <div className="mb-4">
        {pack?.project && (
          <div className="text-[11px] font-mono text-text-muted">
            {pack.project.project_code} · {pack.project.project_name} · {pack.pack_number}
          </div>
        )}
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2 flex-wrap">
          <Camera size={20} className="text-accent" />
          {category.display_name}
          {category.is_custom && (
            <span className="text-[10px] font-mono uppercase tracking-wider2 text-warn px-1.5 py-0.5 rounded bg-warn/10 border border-warn/30">custom</span>
          )}
        </h1>
        {category.description && (
          <p className="text-text-muted text-[13px] mt-1">{category.description}</p>
        )}
        {pack?.label_prefix && (
          <div className="text-[11px] font-mono text-text-dim mt-1">
            label prefix: <span className="text-text-muted">{pack.label_prefix}</span>
          </div>
        )}
      </div>

      {/* Stage B — Share-to-client mode toggle (manager+). When on, ticking a
          photo adds it to a client progress update instead of opening it. */}
      {canManage && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => (shareMode ? shareStore.setActive(false) : shareStore.setActive(true))}
            className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md border transition-colors ${
              shareMode
                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                : "text-text-muted border-line hover:text-text hover:border-accent"
            }`}
          >
            <Share2 size={14} /> {shareMode ? "Stop selecting" : "Share to client"}
          </button>
          {shareMode && (
            <span className="text-[12px] text-text-muted">
              Tick photos to send as a progress update — already-shared ones are skipped. Your selection follows you across categories and packs; send it from the green card.
            </span>
          )}
          {!shareMode && shareSel.size > 0 && (
            <span className="text-[12px] text-emerald-400">{shareSel.size} photo{shareSel.size === 1 ? "" : "s"} selected to send — tap "Share to client" to add more, or send from the green card.</span>
          )}
        </div>
      )}

      {/* Instance tabs (per-instance only) */}
      {isPerInstance && instances.length > 0 && (
        <div className="mb-5 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit overflow-x-auto">
          {instances.map(inst => {
            const active = inst.instance_id === activeInstanceId;
            return (
              <button
                key={inst.instance_id}
                type="button"
                onClick={() => setActiveInstanceId(inst.instance_id)}
                className={`px-3 py-1.5 rounded text-[12px] transition-colors whitespace-nowrap ${
                  active
                    ? "bg-accent/15 text-accent border border-accent/30"
                    : "text-text-muted hover:text-text"
                }`}
              >
                <span className="font-mono">{inst.instance_code}</span>
                {inst.instance_label && (
                  <span className="ml-1 text-text-dim">· {inst.instance_label}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* T-279 — Select-all-on-page bar. Bulk-add every photo currently visible
          for this category/instance to the basket in a single DB round-trip.
          Hidden once everything is already in the basket. */}
      {canBasket && !shareMode && pagePhotoAssetIds.length > 0 && pageUnselectedCount > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-accent/30 bg-accent/5">
          <div className="text-[12px] text-text-muted">
            <span className="font-mono text-text">{pageUnselectedCount}</span>{" "}
            photo{pageUnselectedCount === 1 ? "" : "s"} on this page not yet in the basket
            {isPerInstance && activeInstanceId && (
              <span className="text-text-dim"> · current instance only</span>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { void addManyToBasket(pagePhotoAssetIds); }}
            type="button"
          >
            Select all on page
          </Button>
        </div>
      )}

      {/* Stage B — share-mode select-all-on-page (unshared only). */}
      {shareMode && (() => {
        const unsharedOnPage = pagePhotoAssetIds.filter(id => !alreadyShared.has(id));
        const pickable = unsharedOnPage.filter(id => !shareSel.has(id));
        if (unsharedOnPage.length === 0) return null;
        return (
          <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-emerald-500/30 bg-emerald-500/5">
            <div className="text-[12px] text-text-muted">
              <span className="font-mono text-text">{pickable.length}</span>{" "}
              photo{pickable.length === 1 ? "" : "s"} on this page to add
              {isPerInstance && activeInstanceId && <span className="text-text-dim"> · current instance only</span>}
            </div>
            <Button variant="secondary" size="sm" type="button"
              disabled={pickable.length === 0}
              onClick={() => addManyShare(unsharedOnPage)}>
              Select all on page
            </Button>
          </div>
        );
      })()}

      {/* Subcategories */}
      {subcategories.length === 0 ? (
        <Card>
          <EmptyState
            title="No subcategories on this category"
            description="Custom categories don't auto-include subcategories — add some via SQL seed or use the free-text 'Other' subcategory pattern."
            icon={Paperclip}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {subcategories.map(sub => (
            <SubcategoryCard
              key={sub.pack_subcategory_id}
              sub={sub}
              hopLocal={hopLocalData}
              activeInstanceId={isPerInstance ? activeInstanceId : null}
              assets={(assetsBySubcat.get(sub.pack_subcategory_id) ?? [])}
              signedUrls={signedUrls}
              canUpload={canUpload}
              canDelete={canManage && !["client_accepted", "closed"].includes(pack?.status ?? "")}
              canManage={canManage}
              onToggleNa={(na) => { void setSubcatNa(sub.pack_subcategory_id, na); }}
              canBasket={canBasket}
              basketAssetIds={basketAssetIds}
              onToggleBasket={(assetId) => { void toggleBasket(assetId); }}
              onAddManyToBasket={(assetIds) => { void addManyToBasket(assetIds); }}
              shareMode={shareMode}
              shareSel={shareSel}
              alreadyShared={alreadyShared}
              onToggleShare={toggleShare}
              onAddManyShare={addManyShare}
              onUpload={(files, opts) => handleFiles(files, sub.pack_subcategory_id, sub.code, opts)}
              onOpenPhoto={(asset, siblings) => {
                const idx = siblings.findIndex(s => s.asset_id === asset.asset_id);
                if (idx >= 0) setLightbox({ siblings, index: idx });
              }}
              onDelete={async (asset) => {
                if (!window.confirm(`Delete this ${asset.asset_type}?`)) return;
                try {
                  await deleteHopAsset(asset.asset_id);
                } catch (e: any) {
                  window.alert(`Delete failed: ${e?.message ?? "unknown error"}`);
                  return;
                }
                await refetchAssets();
              }}
            />
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          siblings={lightbox.siblings}
          initialIndex={lightbox.index}
          getUrl={(a) => signedUrls.get(a.asset_id) ?? ""}
          getFullPath={(a) => fullPaths.get(a.asset_id) ?? null}
          packLabelPrefix={pack?.label_prefix}
          onClose={() => setLightbox(null)}
          // T-218 — move-to-category gate matches delete gate (manager+, pack not finalised)
          canMove={canManage && !["client_accepted", "closed"].includes(pack?.status ?? "")}
          onMoveAsset={(a) => setMoveTarget(a)}
        />
      )}

      {/* T-218 — Move-to-category modal */}
      {moveTarget && packId && packCategoryId && (
        <MoveAssetModal
          asset={moveTarget}
          packId={packId}
          currentCategoryId={packCategoryId}
          onClose={() => setMoveTarget(null)}
          onMoved={async () => {
            setMoveTarget(null);
            // Asset has left this category; close the lightbox + refresh.
            setLightbox(null);
            await refetchAssets();
          }}
        />
      )}

      {/* Stage B — send the selected photos as a client progress update. */}
      {sendOpen && projectId && (
        <SendProgressModal
          projectId={projectId}
          userId={user?.user_id ?? null}
          assetIds={[...shareSel]}
          defaultTitle={pack?.project ? `${pack.project.project_code} — HOP Photos` : "HOP Photos"}
          onClose={() => setSendOpen(false)}
          onSent={async ({ count }) => {
            setSendOpen(false);
            exitShareMode();
            await refetchShared();
            window.alert(`Sent ${count} photo${count === 1 ? "" : "s"} to the client portal as a progress update.`);
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SendProgressModal — name the update + send the picked photos to the portal.
// -----------------------------------------------------------------------------
function SendProgressModal({
  projectId, userId, assetIds, defaultTitle, onClose, onSent,
}: {
  projectId: string;
  userId: string | null;
  assetIds: string[];
  defaultTitle: string;
  onClose: () => void;
  onSent: (r: { token: string; count: number }) => void | Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [note, setNote]   = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true); setError(null);
    try {
      const r = await sendProgressUpdate({ projectId, userId, assetIds, title, note, defaultTitle });
      await onSent(r);
    } catch (e: any) { setError(e?.message ?? "Couldn't send the update."); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-ink-card border-t md:border border-line w-full md:max-w-md md:rounded-lg" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2"><Send size={16} className="text-emerald-400" /><h3 className="text-[15px] font-semibold text-text">Send progress update</h3></div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-1.5 rounded-md"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[12px] text-text-muted">
            <span className="font-mono text-text">{assetIds.length}</span> photo{assetIds.length === 1 ? "" : "s"} will be added to this project's client portal as a new dated update.
          </div>
          <label className="block text-[11px] text-text-muted">Update title (optional)
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Frame 6 steel, sectors S1–S3"
              className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" autoFocus />
          </label>
          <label className="block text-[11px] text-text-muted">Note to client (optional)
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="A short line the client sees with this update."
              className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
          </label>
          {error && <div className="p-3 rounded bg-red-900/30 border border-red-900/50 text-[12px] text-red-200 flex items-start gap-2"><AlertTriangle size={12} className="mt-0.5" /><span>{error}</span></div>}
        </div>
        <div className="px-5 py-4 border-t border-line flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={Send} onClick={send} disabled={busy || assetIds.length === 0}>{busy ? "Sending…" : "Send to client"}</Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// SubcategoryCard — one card per subcategory with upload + gallery
// -----------------------------------------------------------------------------
function SubcategoryCard({
  sub, hopLocal, activeInstanceId, assets, signedUrls, canUpload, canDelete, canManage, onToggleNa,
  canBasket, basketAssetIds, onToggleBasket, onAddManyToBasket,
  shareMode, shareSel, alreadyShared, onToggleShare, onAddManyShare,
  onUpload, onOpenPhoto, onDelete,
}: {
  sub: PackSubcategory;
  /** 10 Oct 2026 — this phone's waiting photos, drawn above the grid. */
  hopLocal: HopLocal;
  activeInstanceId: string | null;
  assets: Asset[];
  signedUrls: Map<string, string>;
  canUpload: boolean;
  /** False once pack is submitted/accepted/defects_raised — assets are
      immutable post-submission per migration 046. */
  canDelete: boolean;
  /** Manager+ — can mark this subcategory N/A (not required for this pack). */
  canManage: boolean;
  onToggleNa: (na: boolean) => void;
  /** T-160 — manager+ can flip photos in/out of the acceptance pack basket. */
  canBasket: boolean;
  basketAssetIds: Set<string>;
  onToggleBasket: (assetId: string) => void;
  /** T-279 — bulk-add all (currently-unbasket) photos in this subcategory. */
  onAddManyToBasket: (assetIds: string[]) => void;
  /** Stage B — share-to-client selection overlays the same grid. */
  shareMode: boolean;
  shareSel: Set<string>;
  alreadyShared: Set<string>;
  onToggleShare: (assetId: string) => void;
  onAddManyShare: (assetIds: string[]) => void;
  onUpload: (files: FileList | null, opts?: { isLiveCapture?: boolean; skipWatermark?: boolean }) => void;
  onOpenPhoto: (asset: Asset, siblings: Asset[]) => void;
  onDelete: (asset: Asset) => void;
}) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const photosRef = useRef<HTMLInputElement>(null);
  const docRef    = useRef<HTMLInputElement>(null);
  const preWatermarkedRef = useRef<HTMLInputElement>(null);

  const photos = assets.filter(a => a.asset_type === "photo");
  const docs   = assets.filter(a => a.asset_type === "document");
  // 10 Oct 2026 — old vs today (local day keys, never toISOString)
  const todayKey = dayKeyOf(new Date().toISOString());
  const hasToday = photos.some(p => dayKeyOf(p.taken_at ?? p.captured_at) === todayKey)
                && photos.some(p => dayKeyOf(p.taken_at ?? p.captured_at) !== todayKey);
  const totalCount = assets.length;
  // T-279 — un-basketed photos in this subcategory; drives Select-all button.
  const subcatUnselectedPhotoIds = photos
    .map(p => p.asset_id)
    .filter(id => !basketAssetIds.has(id));
  // Stage B — unshared + not-yet-picked photos in this subcategory.
  const subcatShareableIds = photos
    .map(p => p.asset_id)
    .filter(id => !alreadyShared.has(id) && !shareSel.has(id));

  // N/A — not required for this pack. Collapse to a slim greyed row so the
  // view only shows what actually needs capturing. Manager can restore it.
  if (sub.is_na) {
    return (
      <Card>
        <div className="px-4 py-2.5 flex items-center gap-3 opacity-60">
          <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim px-1.5 py-0.5 rounded border border-line">N/A</span>
          <span className="text-text-muted line-through flex-1 min-w-0 truncate">{sub.display_name}</span>
          <span className="text-[11px] text-text-dim">not required for this pack</span>
          {canManage && (
            <button type="button" onClick={() => onToggleNa(false)}
              className="text-[11px] px-2 py-1 rounded border border-line text-text-muted hover:text-accent hover:border-accent transition-colors flex-shrink-0">
              Mark required
            </button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-4 md:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-text font-medium">{sub.display_name}</span>
              <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">{sub.code}</span>
              {sub.allow_free_text && (
                <span className="text-[9px] font-mono uppercase text-accent tracking-wider2">free-text</span>
              )}
            </div>
            {sub.hint && (
              <div className="text-[11px] text-text-muted mt-1 line-clamp-3">{sub.hint}</div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Stage B — share select-all (unshared) takes over in share mode. */}
            {shareMode ? (
              subcatShareableIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => onAddManyShare(subcatShareableIds)}
                  className="text-[11px] px-2 py-1 rounded border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                  title={`Add ${subcatShareableIds.length} photo${subcatShareableIds.length === 1 ? "" : "s"} to the progress update`}
                >
                  Select all ({subcatShareableIds.length})
                </button>
              )
            ) : (
              /* T-279 — bulk-add all not-yet-basketed photos in this subcategory. */
              canBasket && subcatUnselectedPhotoIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => onAddManyToBasket(subcatUnselectedPhotoIds)}
                  className="text-[11px] px-2 py-1 rounded border border-accent/30 text-accent hover:bg-accent/10 transition-colors"
                  title={`Add ${subcatUnselectedPhotoIds.length} photo${subcatUnselectedPhotoIds.length === 1 ? "" : "s"} to acceptance basket`}
                >
                  Select all ({subcatUnselectedPhotoIds.length})
                </button>
              )
            )}
            <div className="text-[12px] font-mono text-text-muted">
              {totalCount > 0 ? `${totalCount} asset${totalCount === 1 ? "" : "s"}` : "0 assets"}
            </div>
            {/* Mark this subcategory not-required for this pack (manager+). */}
            {canManage && !shareMode && (
              <button
                type="button"
                onClick={() => {
                  if (totalCount > 0 && !window.confirm(`Mark "${sub.display_name}" as N/A? It has ${totalCount} asset${totalCount === 1 ? "" : "s"} — they'll be hidden but not deleted.`)) return;
                  onToggleNa(true);
                }}
                className="text-[11px] px-2 py-1 rounded border border-line text-text-muted hover:text-text hover:border-line-light transition-colors"
                title="Not required for this pack — hides it and drops it from the required count"
              >
                N/A
              </button>
            )}
          </div>
        </div>

        {/* 10 Oct 2026 — photos taken here that are still on this phone */}
        <HopPendingTiles local={hopLocal} packSubcategoryId={sub.pack_subcategory_id} instanceId={activeInstanceId} />

        {/* Upload buttons */}
        {canUpload && (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Button
              variant="secondary"
              size="sm"
              icon={Camera}
              onClick={() => cameraRef.current?.click()}
              type="button"
            >
              Camera
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={ImageIcon}
              onClick={() => photosRef.current?.click()}
              type="button"
            >
              Photos
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={Paperclip}
              onClick={() => docRef.current?.click()}
              type="button"
            >
              Document
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={Upload}
              onClick={() => preWatermarkedRef.current?.click()}
              type="button"
              title="Upload photos already watermarked elsewhere (TimestampCamera etc.) — bytes kept as-is"
            >
              Pre-watermarked
            </Button>
            {/* Hidden file inputs */}
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={e => { onUpload(e.target.files, { isLiveCapture: true }); e.target.value = ""; }}
            />
            <input
              ref={photosRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => { onUpload(e.target.files); e.target.value = ""; }}
            />
            <input
              ref={docRef}
              type="file"
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
              multiple
              className="hidden"
              onChange={e => { onUpload(e.target.files); e.target.value = ""; }}
            />
            {/* T-262 — pre-watermarked photos: bytes kept as-is, EXIF still
                extracted for the row, watermarked=true to suppress CSS overlay. */}
            <input
              ref={preWatermarkedRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => { onUpload(e.target.files, { skipWatermark: true }); e.target.value = ""; }}
            />
          </div>
        )}

        {/* Photo gallery */}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-3">
            {photos.map(p => {
              const url = signedUrls.get(p.asset_id);
              // 10 Oct 2026 — earlier-day photos are dimmed, but only when
              // this subcategory also has photos from today to tell apart.
              const pDay = dayKeyOf(p.taken_at ?? p.captured_at);
              const isOld = hasToday && pDay !== todayKey;
              const inBasket = basketAssetIds.has(p.asset_id);
              const isShared = alreadyShared.has(p.asset_id);
              const isPicked = shareSel.has(p.asset_id);
              return (
                <button
                  key={p.asset_id}
                  type="button"
                  onClick={() => shareMode ? onToggleShare(p.asset_id) : onOpenPhoto(p, photos)}
                  className={`aspect-square relative rounded overflow-hidden border transition-colors group bg-ink-raised ${isOld ? "opacity-55 saturate-50 hover:opacity-100 hover:saturate-100 " : ""}${
                    shareMode
                      ? (isPicked ? "border-emerald-500 ring-2 ring-emerald-500/50" : isShared ? "border-line opacity-60" : "border-line hover:border-emerald-500/60")
                      : (inBasket ? "border-accent ring-2 ring-accent/40" : "border-line hover:border-accent")
                  }`}
                >
                  {url ? (
                    <img
                      src={url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-dim">
                      <Loader2 size={14} className="animate-spin" />
                    </div>
                  )}

                  {isOld && (
                    <div className="absolute bottom-1 right-1 px-1 py-0.5 rounded bg-black/65 text-white text-[9px] font-mono pointer-events-none">
                      {format(parseISO(p.taken_at ?? p.captured_at), "d MMM")}
                    </div>
                  )}
                  {!isOld && hasToday && (
                    <div className="absolute bottom-1 right-1 px-1 py-0.5 rounded bg-ok text-white text-[9px] font-mono pointer-events-none">today</div>
                  )}

                  {/* Stage B — share selection overlay (replaces basket/delete in share mode) */}
                  {shareMode ? (
                    isShared ? (
                      <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-medium inline-flex items-center gap-0.5">
                        <Check size={10} /> shared
                      </div>
                    ) : (
                      <div className={`absolute top-1 left-1 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                        isPicked ? "bg-emerald-500 text-white shadow-md" : "bg-black/40 text-white md:opacity-0 md:group-hover:opacity-100 border border-white/40"
                      }`}>
                        {isPicked ? <Check size={14} /> : <Share2 size={12} />}
                      </div>
                    )
                  ) : (
                    <>
                  {/* T-160 — basket toggle, top-left, manager-only.
                      In basket = filled accent circle with check.
                      Not in basket = outlined circle, only on hover. */}
                  {canBasket && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onToggleBasket(p.asset_id); }}
                      className={`absolute top-1 left-1 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                        inBasket
                          ? "bg-accent text-white shadow-md"
                          : "bg-black/40 text-white md:opacity-0 md:group-hover:opacity-100 hover:bg-black/70 border border-white/40"
                      }`}
                      title={inBasket ? "Remove from acceptance pack" : "Add to acceptance pack"}
                    >
                      {inBasket ? <CheckCircle2 size={14}/> : <ShoppingBag size={12}/>}
                    </button>
                  )}

                  {canDelete && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDelete(p); }}
                      className="absolute top-1 right-1 w-6 h-6 max-md:w-9 max-md:h-9 rounded bg-black/60 text-white md:opacity-0 md:group-hover:opacity-100 hover:bg-danger flex items-center justify-center transition-opacity"
                      title="Delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                    </>
                  )}
                  {p.gps_lat !== null && p.gps_lng !== null && (
                    <div
                      className={`absolute bottom-1 left-1 px-1 py-0.5 rounded text-[9px] font-mono inline-flex items-center gap-0.5 ${
                        p.gps_source === "exif"
                          ? "bg-black/60 text-white"
                          : "bg-amber-500/80 text-black"
                      }`}
                      title={
                        p.gps_source === "exif"
                          ? "GPS from photo EXIF — actual capture location"
                          : p.gps_source === "device_current"
                            ? "GPS from device at upload time — may not be the original capture location"
                            : "GPS from legacy upload (pre-T-199) — provenance unknown"
                      }
                    >
                      <MapPin size={9} /> {p.gps_source === "exif" ? "gps" : "gps?"}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Document list */}
        {docs.length > 0 && (
          <div className="space-y-1.5">
            {docs.map(d => (
              <DocRow
                key={d.asset_id}
                asset={d}
                canDelete={canDelete}
                onDelete={() => onDelete(d)}
              />
            ))}
          </div>
        )}

        {totalCount === 0 && (
          <div className="text-[12px] text-text-muted italic">
            No assets yet — tap a button above to upload.
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
function DocRow({
  asset, canDelete, onDelete,
}: {
  asset: Asset;
  canDelete: boolean;
  onDelete: () => void;
}) {
  async function open() {
    const url = await getAssetSignedUrl(asset.storage_path, 60 * 10);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }
  function fmtBytes(n: number | null): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }
  return (
    <div className="flex items-center gap-3 p-2.5 rounded bg-ink-raised border border-line">
      <Paperclip size={14} className="text-text-muted flex-shrink-0" />
      <button onClick={open} type="button" className="flex-1 min-w-0 text-left text-[13px] text-text hover:text-accent truncate">
        {asset.file_name || asset.storage_path.split("/").pop()}
      </button>
      <span className="text-[11px] text-text-muted font-mono flex-shrink-0">
        {fmtBytes(asset.file_size_bytes)}
      </span>
      {canDelete && (
        <button onClick={onDelete} type="button" className="text-text-muted hover:text-danger flex-shrink-0 p-1" title="Delete">
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Lightbox — fullscreen photo view with metadata
// -----------------------------------------------------------------------------
function Lightbox({ siblings, initialIndex, getUrl, getFullPath, packLabelPrefix, onClose, canMove, onMoveAsset }: {
  siblings: Asset[];
  initialIndex: number;
  getUrl: (a: Asset) => string;
  /** mig 441 — R2 path of this photo's full-size copy, if it has landed. */
  getFullPath?: (a: Asset) => string | null;
  packLabelPrefix?: string | null;
  onClose: () => void;
  // T-218 — move-to-category action; only rendered when canMove is true.
  canMove?: boolean;
  onMoveAsset?: (asset: Asset) => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const asset = siblings[index];
  // mig 442 — getUrl is the small tile now. Show it at once, then swap in the
  // real photo, signed when it is opened.
  const [bigUrl, setBigUrl] = useState<{ id: string; url: string } | null>(null);
  useEffect(() => {
    let alive = true;
    if (!asset) return;
    getAssetSignedUrl(asset.storage_path, 60 * 10).then(u => { if (alive && u) setBigUrl({ id: asset.asset_id, url: u }); });
    return () => { alive = false; };
  }, [asset?.asset_id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const url = (asset && bigUrl?.id === asset.asset_id ? bigUrl.url : "") || getUrl(asset);
  const prev = () => setIndex(i => (i - 1 + siblings.length) % siblings.length);
  const next = () => setIndex(i => (i + 1) % siblings.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowLeft")  prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, siblings.length]);

  if (!asset) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Top-right action cluster: Move (T-218) sits next to Close. */}
      <div className="absolute right-4 flex items-center gap-2 z-10" style={{ top: "calc(var(--safe-top, 0px) + 1rem)" }}>
        {canMove && onMoveAsset && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMoveAsset(asset); }}
            className="h-9 px-3 rounded-full bg-black/60 text-white inline-flex items-center gap-1.5 text-[12px] hover:bg-black"
            title="Move this photo to a different category"
          >
            <FolderInput size={14} /> Move
          </button>
        )}
        {getFullPath?.(asset) && (
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              const u = await getAssetSignedUrl(getFullPath(asset)!, 60 * 10);
              if (u) window.open(u, "_blank", "noopener");
            }}
            className="h-9 px-3 rounded-full bg-black/60 text-white inline-flex items-center gap-1.5 text-[12px] hover:bg-black"
            title="Open the full-size copy (kept for our own review — the client pack uses the smaller one)"
          >
            <Download size={14} /> Full size
          </button>
        )}
        <a
          href={url}
          download={asset.file_name ?? undefined}
          onClick={e => e.stopPropagation()}
          className="w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black"
          title="Download"
        >
          <Download size={18} />
        </a>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black"
        >
          <X size={18} />
        </button>
      </div>

      {/* T-215 — prev/next nav (only when more than one sibling) */}
      {siblings.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black z-10"
            title="Previous (←)"
          >
            <ChevronLeft size={22}/>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black z-10"
            title="Next (→)"
          >
            <ChevronRight size={22}/>
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded bg-black/60 text-white text-[11px] font-mono">
            {index + 1} / {siblings.length}
          </div>
        </>
      )}

      <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
        <img
          src={url}
          alt=""
          className="max-w-full max-h-[88vh] object-contain rounded shadow-2xl"
        />

        {/* T-217 step 3 — CSS-overlay watermark for new (non-burned-in) uploads.
            Strict rule (James 2026-05-07): all three of label + date + GPS
            must be present, OR no watermark at all. If any one is missing,
            render nothing (better blank than misleading partial info).
            Legacy watermarked photos unchanged (burned-in remains visible). */}
        {!asset.watermarked
          && packLabelPrefix
          && asset.taken_at
          && asset.gps_lat !== null
          && asset.gps_lng !== null && (
          <div
            className="absolute bottom-4 left-4 px-3 py-2 bg-black/55 border border-white/15 rounded font-mono leading-tight pointer-events-none"
            style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.6)" }}
          >
            <div className="text-accent text-[14px] font-medium">{packLabelPrefix}</div>
            <div className="text-white/95 text-[12px]">
              {format(parseISO(asset.taken_at), "dd/MM/yyyy HH:mm")}
            </div>
            <div className="text-white/95 text-[12px]">
              {asset.gps_lat.toFixed(6)}, {asset.gps_lng.toFixed(6)}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// =============================================================================
// MoveAssetModal — T-218
//
// Lets manager+ relocate a single hop_asset from its current category /
// subcategory to a different one within the SAME pack. Cross-pack moves are
// out of scope (different audit/RLS surface).
//
// Direct UPDATE on hop_assets — RLS (mig 039 hop_assets_update) already lets
// manager+ patch any asset in projects they're on, so no new RPC needed.
// pack_subcategory_id is set independently and may be null when the target
// category has no subcategories OR the user picks "(no subcategory)".
// =============================================================================
function MoveAssetModal({
  asset, packId, currentCategoryId, onClose, onMoved,
}: {
  asset: Asset;
  packId: string;
  currentCategoryId: string;
  onClose: () => void;
  onMoved: () => void | Promise<void>;
}) {
  const [targetCatId, setTargetCatId]       = useState<string>("");
  const [targetSubcatId, setTargetSubcatId] = useState<string>("");   // "" = no subcategory
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  // All other categories on this pack (current excluded — can't "move" to where it already is)
  const { data: targetCategories = [] } = useQuery({
    queryKey: ["hop_pack_all_categories_for_move", packId, currentCategoryId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_categories")
        .select("pack_category_id, code, display_name, sort_order")
        .eq("pack_id", packId)
        .neq("pack_category_id", currentCategoryId)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Subcategories of the chosen target category
  const { data: targetSubcats = [] } = useQuery({
    queryKey: ["hop_pack_subcats_for_move_target", targetCatId],
    enabled: !!targetCatId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_subcategories")
        .select("pack_subcategory_id, code, display_name, sort_order")
        .eq("pack_category_id", targetCatId)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
  });

  // When category changes, reset subcategory pick
  useEffect(() => { setTargetSubcatId(""); }, [targetCatId]);

  async function handleMove() {
    if (!targetCatId) {
      setError("Pick a target category first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error: e } = await supabase
      .from("hop_assets")
      .update({
        pack_category_id:    targetCatId,
        pack_subcategory_id: targetSubcatId || null,
      })
      .eq("asset_id", asset.asset_id);
    setSubmitting(false);
    if (e) {
      setError(`Move failed: ${e.message}`);
      return;
    }
    await onMoved();
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-line">
          <h2 className="font-semibold text-[15px]">Move photo to a different category</h2>
          <p className="text-[12px] text-text-muted mt-1">
            Pick the new home for this photo. It'll disappear from the current category.
          </p>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-1">
              Target category
            </label>
            <select
              value={targetCatId}
              onChange={(e) => setTargetCatId(e.target.value)}
              className="w-full bg-ink-raised border border-line rounded px-3 py-2 text-[13px] focus:border-accent outline-none"
            >
              <option value="">— Choose a category —</option>
              {targetCategories.map((c: any) => (
                <option key={c.pack_category_id} value={c.pack_category_id}>
                  {c.display_name}
                </option>
              ))}
            </select>
            {targetCategories.length === 0 && (
              <div className="text-[11px] text-text-muted mt-1 italic">
                No other categories on this pack — nowhere to move to.
              </div>
            )}
          </div>

          {targetCatId && (
            <div>
              <label className="block text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-1">
                Subcategory <span className="text-text-dim normal-case font-sans tracking-normal">(optional)</span>
              </label>
              <select
                value={targetSubcatId}
                onChange={(e) => setTargetSubcatId(e.target.value)}
                className="w-full bg-ink-raised border border-line rounded px-3 py-2 text-[13px] focus:border-accent outline-none"
              >
                <option value="">(no subcategory)</option>
                {targetSubcats.map((s: any) => (
                  <option key={s.pack_subcategory_id} value={s.pack_subcategory_id}>
                    {s.display_name}
                  </option>
                ))}
              </select>
              {targetSubcats.length === 0 && (
                <div className="text-[11px] text-text-muted mt-1 italic">
                  This category has no subcategories — photo will sit at the category level.
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="text-[12px] text-danger flex items-start gap-2">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            icon={FolderInput}
            size="sm"
            onClick={handleMove}
            disabled={submitting || !targetCatId}
          >
            {submitting ? "Moving…" : "Move photo"}
          </Button>
        </div>
      </div>
    </div>
  );
}
